import { useCallback, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { zodResolver } from '@hookform/resolvers/zod';
import { MimeType, Task, WORD_TRANSLATION_PROVIDER_ID } from '@repo/shared';
import { useRouter } from '@tanstack/react-router';
import { Brain } from 'lucide-react';
import { toast } from 'sonner';

import { useCreateTextTranslationWithFile } from '@/api/generated';
import type { CreateTextTranslationWithFileRequest } from '@/api/generated.schemas';
import LanguageCombobox from '@/components/language-combobox';
import ModelCombobox from '@/components/model-combobox';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardFooter } from '@/components/ui/card';
import { FileInput } from '@/components/ui/file-input';
import FileUpload from '@/components/ui/file-upload';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Spinner } from '@/components/ui/spinner';
import {
  type CreateTextTranslationWithFileSchema,
  createTextTranslationWithFileSchema,
} from '@/routes/(app)/(tasks)/lib/text-translation-schema';

const TextTranslationWithFile = () => {
  const { mutate, isPending } = useCreateTextTranslationWithFile();
  const form = useForm<CreateTextTranslationWithFileSchema>({
    defaultValues: {
      files: [],
      sourceLanguageCode: '',
      targetLanguageCode: '',
      model: WORD_TRANSLATION_PROVIDER_ID,
    },
    resolver: zodResolver(createTextTranslationWithFileSchema),
  });
  const filesWatch = form.watch('files');

  const blockParameters = useMemo(
    () => filesWatch.some((file) => file.type === MimeType.JSON),
    [filesWatch]
  );

  const { t } = useTranslation();
  const router = useRouter();
  const handleSubmit = useCallback(
    (values: CreateTextTranslationWithFileSchema) => {
      const formData = new FormData();
      // looks wrong, but it is correct.
      values.files.forEach((file) => {
        formData.append('files', file);
      });
      const body: Omit<CreateTextTranslationWithFileSchema, 'files'> = {
        sourceLanguageCode: values.sourceLanguageCode,
        targetLanguageCode: values.targetLanguageCode,
        model: values.model,
      };
      formData.append('data', JSON.stringify(body));

      mutate(
        {
          data: formData as unknown as CreateTextTranslationWithFileRequest,
        },
        {
          onSuccess: async (data) => {
            toast.success(t('clusters.createModelRun.success'));
            await router.navigate({
              to: '/app/clusters',
              search: { datasetId: data.dataset.id, isEval: false },
            });
          },
          onError: () => {
            toast.error(t('clusters.createModelRun.error'));
          },
        }
      );
    },
    [mutate, router, t]
  );

  const handleRemoveFile = useCallback(
    (file: File) => {
      form.setValue(
        'files',
        form.getValues().files.filter((_file) => _file !== file)
      );
    },
    [form]
  );

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)}>
        <Card>
          <CardContent className='flex flex-col gap-4'>
            <FormField
              control={form.control}
              name='files'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('datasets.createDataset.file')}</FormLabel>
                  <FormControl>
                    <FileInput
                      {...field}
                      mimeTypes={[MimeType.DOC, MimeType.DOCX]}
                      multiple
                    />
                  </FormControl>
                </FormItem>
              )}
            />
            {filesWatch.map((file) => (
              <FileUpload
                key={file.name}
                id={file.name}
                name={file.name}
                size={file.size}
                onRemove={() => {
                  handleRemoveFile(file);
                }}
              />
            ))}
            <FormField
              control={form.control}
              name='model'
              disabled={blockParameters}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t('tasks.textTranslation.form.model.label')}
                  </FormLabel>
                  <FormControl>
                    <ModelCombobox
                      {...field}
                      task={Task.TEXT_TRANSLATION}
                      placeholder={t(
                        'tasks.textTranslation.form.model.placeholder'
                      )}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name='sourceLanguageCode'
              disabled={blockParameters}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t('tasks.textTranslation.form.sourceLanguageCode.label')}
                  </FormLabel>
                  <FormControl>
                    <LanguageCombobox
                      {...field}
                      placeholder={t(
                        'tasks.textTranslation.form.sourceLanguageCode.placeholder'
                      )}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name='targetLanguageCode'
              disabled={blockParameters}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t('tasks.textTranslation.form.targetLanguageCode.label')}
                  </FormLabel>
                  <FormControl>
                    <LanguageCombobox
                      {...field}
                      placeholder={t(
                        'tasks.textTranslation.form.targetLanguageCode.placeholder'
                      )}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </CardContent>
          <CardFooter>
            <Button
              type='submit'
              disabled={isPending}>
              <Button.Icon side={'left'}>
                {isPending ? <Spinner className='text-inherit' /> : <Brain />}
              </Button.Icon>
              {t('tasks.textTranslation.form.submit')}
            </Button>
          </CardFooter>
        </Card>
      </form>
    </Form>
  );
};

export default TextTranslationWithFile;
